import { useQuery } from "@apollo/client";
import { Box, CircularProgress } from "@mui/material";
import { DateTime } from "luxon";
import { ReactElement, useEffect, useRef, useState } from "react";

import {
  GetAudienceContactFieldsQuery,
  GetSubscriberSummaryQuery,
  GetSubscriberSummaryQueryVariables,
  ListSubscribersQuery,
  ListSubscribersQueryVariables,
} from "~frontend/__generated__/graphql";
import { AsyncWrapper } from "~frontend/components/AsyncWrapper";
import { FlexBox } from "~frontend/components/FlexBox";
import { LinkButton } from "~frontend/components/Link";
import { PageHeader } from "~frontend/components/PageHeader";
import { TableControls } from "~frontend/components/Table";
import { GET_AUDIENCE_CONTACT_FIELDS } from "~frontend/graphql/audience";
import {
  GET_SUBSCRIBER_SUMMARY,
  LIST_SUBSCRIBERS,
} from "~frontend/graphql/shortcode";
import { useFeatureFlag } from "~frontend/providers/LoggedInUserProvider/context";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";
import { formatShortDateTime } from "~frontend/util/dateFormat";
import { jobFailed, jobInProgress } from "~frontend/util/jobs";
import { cleanPhoneNumber, yupPhoneNumber } from "~frontend/util/phoneNumber";

import AddSubscriberDialog from "./AddSubscriberDialog";
import JobDetailsDialog from "./JobDetailsDialog";
import { ManageCustomFieldsDialog } from "./ManageCustomFieldsDialog";
import SubscribersTable from "./SubscribersTable";
import { Shortcode } from "./types";

export default function Subscribers({
  shortcode,
  startShortcodePolling,
  stopShortcodePolling,
}: {
  shortcode: Shortcode;
  startShortcodePolling: (interval: number) => void;
  stopShortcodePolling: () => void;
}): ReactElement {
  const [activeStatusFilter, setActiveStatusFilter] = useState<boolean | null>(
    null
  );
  const [search, setSearch] = useState<string>("");

  const [addSubscriberDialogOpen, setAddSubscriberDialogOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [jobDetailsOpen, setJobDetailsOpen] = useState(false);

  const subaccount = useSubaccount();
  const { data: audienceFields } = useQuery<GetAudienceContactFieldsQuery>(
    GET_AUDIENCE_CONTACT_FIELDS,
    {
      variables: {
        input: { opnId: shortcode.id, subaccountId: subaccount?.subaccount.id },
      },
    }
  );

  const summaryQuery = useQuery<
    GetSubscriberSummaryQuery,
    GetSubscriberSummaryQueryVariables
  >(GET_SUBSCRIBER_SUMMARY, {
    variables: {
      input: {
        shortcodeId: shortcode.id,
      },
    },
    fetchPolicy: "network-only",
  });

  const subscriberQuery = useQuery<
    ListSubscribersQuery,
    ListSubscribersQueryVariables
  >(LIST_SUBSCRIBERS, {
    variables: {
      input: {
        shortcodeId: shortcode.id,
        activeStatusFilter,
        limit: rowsPerPage,
        offset: page * rowsPerPage,
        subscriberPhoneNumber: search ? cleanPhoneNumber(search) : null,
      },
    },
    fetchPolicy: "network-only",
  });

  // When an import job is running, poll for updates. When it completes, refetch the subscriber list
  // and summary.
  const jobWasRunning = useRef(false);
  useEffect(() => {
    const importJob = shortcode.subscriberImportJob;
    if (!importJob) {
      stopShortcodePolling();
      return;
    }

    const isRunning = jobInProgress(importJob);
    if (isRunning) {
      startShortcodePolling(1000);
    } else {
      stopShortcodePolling();
      if (jobWasRunning.current) {
        subscriberQuery.refetch().catch(console.error);
        summaryQuery.refetch().catch(console.error);
      }
    }

    jobWasRunning.current = isRunning;

    return () => {
      stopShortcodePolling();
    };
  }, [
    shortcode.subscriberImportJob,
    startShortcodePolling,
    stopShortcodePolling,
    subscriberQuery,
    summaryQuery,
  ]);

  function renderSubscriberSummary() {
    if (summaryQuery.loading) {
      return (
        <>
          <CircularProgress size={16} />
          Loading subscriber summary...
        </>
      );
    }

    if (summaryQuery.error) {
      return <>Error loading subscriber summary</>;
    }

    return (
      <FlexBox gap={3}>
        <Box>
          {summaryQuery.data!.getSubscriberSummary.activeSubscribers} Active
          Subscribers
        </Box>
        <Box>
          {summaryQuery.data!.getSubscriberSummary.unsubscribedSubscribers}{" "}
          Unsubscribed
        </Box>
      </FlexBox>
    );
  }

  function renderImportJob() {
    const importJob = shortcode.subscriberImportJob;
    if (!importJob) {
      return null;
    }

    let message: string;
    if (jobInProgress(importJob)) {
      message = `Upload in progress...`;
    } else if (jobFailed(importJob).failed) {
      message = `Upload Failed: ${formatShortDateTime(DateTime.fromMillis(importJob.startedAt))}`;
    } else {
      message = `Last Upload: ${formatShortDateTime(DateTime.fromMillis(importJob.startedAt))}`;
    }
    return (
      <Box>
        {message}{" "}
        <LinkButton onClick={() => setJobDetailsOpen(true)}>
          View Details
        </LinkButton>
      </Box>
    );
  }

  let totalCount: number = 0;
  if (summaryQuery.data) {
    const { activeSubscribers, unsubscribedSubscribers } =
      summaryQuery.data.getSubscriberSummary;
    if (activeStatusFilter === true) {
      totalCount = activeSubscribers;
    } else if (activeStatusFilter === false) {
      totalCount = unsubscribedSubscribers;
    } else {
      totalCount = activeSubscribers + unsubscribedSubscribers;
    }
  }

  const customFieldsAllowed = useFeatureFlag("audienceCustomFields");
  const [customFieldsDialogOpen, setCustomFieldsDialogOpen] = useState(false);

  return (
    <Box>
      <FlexBox justifyContent="space-between" alignItems="center" gap={3}>
        <FlexBox justifyContent="flex-start" alignItems="center" gap={3}>
          <PageHeader>Subscribers</PageHeader>
          <Box sx={{ mb: 1 }}>{renderSubscriberSummary()}</Box>
        </FlexBox>
        <Box>{renderImportJob()}</Box>
      </FlexBox>
      <TableControls
        addButtons={[
          {
            id: "add-subscriber",
            label: "Add Subscribers",
            onClick: () => setAddSubscriberDialogOpen(true),
          },
        ]}
        secondaryButtons={
          customFieldsAllowed
            ? [
                {
                  id: "manage-custom-fields",
                  label: "Manage Custom Fields",
                  onClick: () => setCustomFieldsDialogOpen(true),
                  icon: null,
                },
              ]
            : []
        }
        filters={[
          {
            id: "active-status",
            label: "Subscription Status",
            onChange: (newStatusFilter: string) => {
              if (newStatusFilter === "active") {
                setActiveStatusFilter(true);
              } else if (newStatusFilter === "unsubscribed") {
                setActiveStatusFilter(false);
              } else if (newStatusFilter === "all") {
                setActiveStatusFilter(null);
              }
            },
            options: [
              {
                value: "all",
                label: "All",
              },
              {
                value: "active",
                label: "Subscribed",
              },
              {
                value: "unsubscribed",
                label: "Unsubscribed",
              },
            ],
            value:
              activeStatusFilter === true
                ? "active"
                : activeStatusFilter === false
                  ? "unsubscribed"
                  : "all",
          },
        ]}
        search={{
          onChange: setSearch,
          placeholder: "Search by phone number",
          value: search,
          schema: yupPhoneNumber,
        }}
      />
      <AsyncWrapper
        loading={subscriberQuery.loading}
        errors={[subscriberQuery.error]}
        data={subscriberQuery.data}
      >
        {data => (
          <SubscribersTable
            shortcode={shortcode}
            subscribers={data.listSubscribers}
            page={page}
            setPage={setPage}
            rowsPerPage={rowsPerPage}
            setRowsPerPage={setRowsPerPage}
            totalCount={totalCount}
            audienceContactFields={
              audienceFields?.getAudienceContactFields || []
            }
          />
        )}
      </AsyncWrapper>
      {addSubscriberDialogOpen && (
        <AddSubscriberDialog
          onClose={() => setAddSubscriberDialogOpen(false)}
          shortcodeId={shortcode.id}
          refreshList={() => subscriberQuery.refetch()}
          openJobDetails={() => setJobDetailsOpen(true)}
          audienceContactFields={audienceFields?.getAudienceContactFields || []}
        />
      )}
      {jobDetailsOpen && shortcode.subscriberImportJob && (
        <JobDetailsDialog
          job={shortcode.subscriberImportJob}
          onClose={() => setJobDetailsOpen(false)}
        />
      )}
      {customFieldsDialogOpen && shortcode.subscriberAudienceId && (
        <ManageCustomFieldsDialog
          opnId={shortcode.id}
          audienceId={shortcode.subscriberAudienceId}
          onClose={() => setCustomFieldsDialogOpen(false)}
          existingFields={audienceFields?.getAudienceContactFields || []}
        />
      )}
    </Box>
  );
}
